/**
 * Donor Dashboard Page
 *
 * Comprehensive dashboard for authenticated donors showing:
 * - Donation summary and statistics
 * - Charts and visualizations
 * - Recent activity
 * - Quick actions
 * - Community impact metrics
 */

import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  HeartIcon,
  //CreditCardIcon,
  //DocumentTextIcon,
  CalendarIcon,
  //ChartBarIcon,
  BellIcon,
  //ClockIcon,
  TrophyIcon,
  //UsersIcon,
  //BuildingLibraryIcon,
} from '@heroicons/react/24/outline';
import { DonationSummaryCard } from '@/components/features/dashboard/DonationSummaryCard';
import { DonationTrendChart } from '@/components/features/dashboard/DonationTrendChart';
import { DonationCategoryChart } from '@/components/features/dashboard/DonationCategoryChart';
import { RecentDonationsTable } from '@/components/features/dashboard/RecentDonationsTable';
import { ImpactMetricsCard } from '@/components/features/dashboard/ImpactMetricsCard';
import { UpcomingEventsCard } from '@/components/features/dashboard/UpcomingEventsCard';
import { PrayerTimesCard } from '@/components/features/dashboard/PrayerTimesCard';
import { QuickActionsCard } from '@/components/features/dashboard/QuickActionsCard';
import { TaxReceiptCard } from '@/components/features/dashboard/TaxReceiptCard';
import { RecurringDonationsCard } from '@/components/features/dashboard/RecurringDonationsCard';
import { PledgesSummaryCard } from '@/components/features/dashboard/PledgesSummaryCard';
import { PledgesListCard } from '@/components/features/dashboard/PledgesListCard';
import { useCompany } from '@/hooks/useCompany';
import { usePledges } from '@/hooks/usePledges';
import {
  useDashboardOverview,
  useDashboardCharts,
  useDashboardCommunity,
} from '@/hooks/useDashboard';
import companyLogo from '@/assets/images/10001_logo.png';

export function DonorDashboard() {
  const navigate = useNavigate();
  const { company } = useCompany();
  const [selectedPeriod, setSelectedPeriod] = useState<string>('12');

  // Calculate months based on selected period
  const getMonths = (period: string): number => {
    if (period === 'current-year') {
      const currentMonth = new Date().getMonth() + 1; // 1-12
      return currentMonth;
    }
    return parseInt(period, 10);
  };

  const months = getMonths(selectedPeriod);

  // Wait for both Auth0 session restore AND the token getter registration before
  // firing any auth-required requests.  isAuthReady is false until the AuthProvider
  // effect that calls setTokenGetter completes, which prevents child-effect API
  // calls from racing ahead of the token getter.
  const { user, donor, isDonorLoading, isLoading: authIsLoading, isAuthReady } = useAuth();
  const authReady = isAuthReady && !authIsLoading;

  const overview = useDashboardOverview({ limit: 5, autoFetch: authReady });
  const charts = useDashboardCharts({ period: 'monthly', months, autoFetch: authReady });
  const community = useDashboardCommunity(); // public endpoints — no auth gate needed

  // Fetch this donor's pledges once donor data is available
  const { companyIdNumber } = useCompany();
  const pledgesData = usePledges(companyIdNumber ?? undefined, {
    donorId: donor?.donorId,
    autoFetch: authReady && !!companyIdNumber,
  });

  const isLoading =
    !isAuthReady ||
    authIsLoading ||
    overview.isLoading ||
    charts.isLoading ||
    community.isLoading ||
    isDonorLoading;
  const hasError = overview.error || charts.error || community.error;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }
  if (hasError) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-2/3">
          <div className="text-red-600 mb-4">
            <svg
              className="h-12 w-12 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to Load Dashboard</h3>
          <p className="text-gray-600 mb-4">
            {overview.error?.message || charts.error?.message || community.error?.message}
          </p>
          <button
            onClick={() => {
              overview.refetch();
              charts.refetch();
              community.refetch();
            }}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          {company?.companyName && (
            <img
              src={companyLogo}
              alt={company.companyName}
              className="h-14 w-14 rounded-b-lg object-contain shrink-0 shadow-sm border border-gray-100"
            />
          )}
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Welcome Back
              {donor?.firstName ? `, ${donor.firstName}` : user?.name ? `, ${user.name}` : ''}!
            </h2>
            <p className="mt-1 text-gray-600 font-semibold">
              Welcome to {company?.companyName || 'our organization'} Donation Portal. Here's what's
              happening with your donations
            </p>
          </div>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={() => navigate('/donate')}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium shadow-sm"
          >
            <HeartIcon className="h-5 w-5 mr-2" />
            Make a Donation
          </button>
        </div>
      </div>

      {/* Summary Stats - 5 Cards */}
      {overview.data && (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          <DonationSummaryCard
            title="This Year"
            amount={overview.data.summary.this_year.total_amount}
            subtitle={`${overview.data.summary.this_year.total_count} payments`}
            icon={HeartIcon}
            color="primary"
            trend={
              overview.data.summary.this_year.trend_percentage !== null &&
              overview.data.summary.this_year.trend_direction !== 'flat'
                ? {
                    value: Math.abs(overview.data.summary.this_year.trend_percentage),
                    direction: overview.data.summary.this_year.trend_direction as 'up' | 'down',
                  }
                : undefined
            }
          />
          <DonationSummaryCard
            title="Lifetime Total"
            amount={overview.data.summary.lifetime.donation_amount}
            subtitle={`${overview.data.summary.lifetime.donation_count} donations - since ${new Date(overview.data.summary.lifetime.start_date).getFullYear()}`}
            icon={TrophyIcon}
            color="green"
          />
          <DonationSummaryCard
            title="This Month"
            amount={overview.data.summary.this_month.donation_amount}
            subtitle={`${overview.data.summary.this_month.donation_count} donations`}
            icon={CalendarIcon}
            color="blue"
            trend={
              overview.data.summary.this_month.trend_percentage !== null &&
              overview.data.summary.this_month.trend_direction !== 'flat'
                ? {
                    value: Math.abs(overview.data.summary.this_month.trend_percentage),
                    direction: overview.data.summary.this_month.trend_direction as 'up' | 'down',
                  }
                : undefined
            }
          />
          {/*           
          <DonationSummaryCard
            title="Tax Deductible"
            amount={overview.data.summary.tax_year.donation_amount}
            subtitle={`${new Date(overview.data.summary.tax_year.start_date).getFullYear()} Year-to-date`}
            icon={DocumentTextIcon}
            color="purple"
          /> */}
          <div className="sm:col-span-2 lg:col-span-1 xl:col-span-2 h-full">
            <PledgesSummaryCard pledgesData={pledgesData} />
          </div>
        </div>
      )}

      {/* Charts Row */}
      {charts.data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DonationTrendChart
            months={months}
            selectedPeriod={selectedPeriod}
            onPeriodChange={setSelectedPeriod}
            chartsData={charts.data}
          />
          <DonationCategoryChart months={months} chartsData={charts.data} />
        </div>
      )}

      {/* Middle Section: Quick Actions, Prayer Times, Tax Receipt */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <QuickActionsCard />
        <PrayerTimesCard />
        <TaxReceiptCard overviewData={overview.data ?? null} />
      </div>

      {/* Community Impact & Upcoming Events */}
      {community.data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ImpactMetricsCard />
          <UpcomingEventsCard />
        </div>
      )}

      {/* Recurring Donations Management */}
      {overview.data?.recurring_donations && (
        <RecurringDonationsCard overviewData={overview.data} />
      )}

      {/* Pledges List */}
      <PledgesListCard pledgesData={pledgesData} />

      {/* Recent Donations Table */}
      {overview.data?.recent_donations && <RecentDonationsTable overviewData={overview.data} />}

      {/* Announcements/Updates Section */}
      {community.data?.updates?.updates && community.data.updates.updates.length > 0 && (
        <div className="bg-linear-to-r from-primary-50 to-blue-50 rounded-lg p-6 shadow-sm border border-primary-100">
          <div className="flex items-start space-x-4">
            <div className="shrink-0">
              <BellIcon className="h-6 w-6 text-primary-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">Community Updates</h3>
              <div className="mt-3 space-y-3">
                {community.data.updates.updates.map((update) => (
                  <div key={update.id} className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{update.title}</p>
                        <p className="text-sm text-gray-600 mt-1">{update.content}</p>
                        {update.author && (
                          <p className="text-xs text-gray-400 mt-1">— {update.author}</p>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 ml-4">
                        {new Date(update.date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
